import {
  Sorting,
  ColumnWidthInfo,
  ColumnExtension,
  GridFilter,
} from "../../common/store/types";
import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { SatelliteMachine, SortOrder } from "../../../api/graphql";
import type { DataGridColumn } from "../../../common/controls/lists/DataGrid";
import {
  isOperatorAvailable,
  type FilterOperator,
} from "../../../common/services/mrtFilterService";
import { userDisplayId } from "../../user/store/UserCommonStore";
import { satelliteTaskDisplayId } from "../../satelliteTask/store/SatelliteTaskCommonStore";

export type ColumnInfo = Record<
  string,
  {
    sortingEnabled: boolean;
    width: number;
    isHidden: boolean;
  }
>;

const VERSION = "1.0.0";

export type RowClickViewMode = "route" | "drawer";

export type SatelliteMachineListProfile = {
  VERSION: string;
  columnOrder: string[];
  columnsInfo: ColumnInfo;
  sorting: Sorting[];
  filtering: GridFilter[];
  showColumnFilters?: boolean;
  viewMode?: RowClickViewMode;
};

export class SatelliteMachineGridDataStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  errorDescription: string = "";
  errorDialogOpened: boolean = false;
  filtering: GridFilter[] = [];

  // Estado de UI del DataGrid (sincronizado con ListToolbox)
  showColumnFilters: boolean = false;
  isFullScreen: boolean = false;
  columnMenuOpen: boolean = false;
  viewMode: RowClickViewMode = "route";

  get ProfileKey(): string[] {
    return ["SatelliteMachine", "list"];
  }

  columnsInfo: ColumnInfo = {};
  initialColumnsInfo: ColumnInfo = {};

  sortExpression: Record<
    string,
    (direction: SortOrder, lang: LangStore) => any
  > = {
    authorizedBy: (direction: SortOrder, _lang: LangStore) => {
      return { authorizedBy: { email: direction } };
    },
    suspendedBy: (direction: SortOrder, _lang: LangStore) => {
      return { suspendedBy: { email: direction } };
    },
    tasks: (direction: SortOrder, _lang: LangStore) => {
      return { tasks: { _count: direction } };
    },
  };

  initialColumnOrder = [
    "name",
    "satelliteType",
    "region",
    "status",
    "enabled",
    "publicIp",
    "lastHeartbeatAt",
    "machineCode",
    "certificateExpiresAt",
    "authTokenLastFour",
    "authTokenCreatedAt",
    "authTokenRotatedAt",
    "authorizedAt",
    "authorizedBy",
    "suspendedAt",
    "suspendedBy",
    "suspensionReason",
    "allowedTenantIds",
    "proxyEnabled",
    "proxyPort",
    "proxyProtocols",
    "botEnabled",
    "botMaxConcurrent",
    "workerEnabled",
    "location",
    "countryCode",
    "lastHeartbeatMetrics",
    "connectedAt",
    "disconnectedAt",
    "tunnelConnectedAt",
    "tasks",
    "id",
  ];
  columnOrder = [
    "name",
    "satelliteType",
    "region",
    "status",
    "enabled",
    "publicIp",
    "lastHeartbeatAt",
    "machineCode",
    "certificateExpiresAt",
    "authTokenLastFour",
    "authTokenCreatedAt",
    "authTokenRotatedAt",
    "authorizedAt",
    "authorizedBy",
    "suspendedAt",
    "suspendedBy",
    "suspensionReason",
    "allowedTenantIds",
    "proxyEnabled",
    "proxyPort",
    "proxyProtocols",
    "botEnabled",
    "botMaxConcurrent",
    "workerEnabled",
    "location",
    "countryCode",
    "lastHeartbeatMetrics",
    "connectedAt",
    "disconnectedAt",
    "tunnelConnectedAt",
    "tasks",
    "id",
  ];

  _sorting: Sorting[];

  // Restricciones de rol por columna (columnas que requieren roles específicos)
  readonly columnRoleRestrictions: Record<string, string[]> = {};

  // Columna de grilla → campo real por el que filtrar. Las pseudo-columnas del
  // snapshot (`data_.X`) no existen en SQL: filtran por la columna promovida
  // con .searchable.
  readonly columnFilterAlias: Record<string, string> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.columnsInfo = {
      name: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      satelliteType: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      region: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      status: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      enabled: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      publicIp: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      lastHeartbeatAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      machineCode: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      certificateExpiresAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authTokenLastFour: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authTokenCreatedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authTokenRotatedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authorizedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authorizedBy: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      suspendedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      suspendedBy: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      suspensionReason: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      allowedTenantIds: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      proxyEnabled: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      proxyPort: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      proxyProtocols: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      botEnabled: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      botMaxConcurrent: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      workerEnabled: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      location: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      countryCode: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      lastHeartbeatMetrics: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      connectedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      disconnectedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      tunnelConnectedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      tasks: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      id: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
    };
    this.initialColumnsInfo = {
      name: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      satelliteType: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      region: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      status: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      enabled: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      publicIp: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      lastHeartbeatAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: false,
      },
      machineCode: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      certificateExpiresAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authTokenLastFour: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authTokenCreatedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authTokenRotatedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authorizedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      authorizedBy: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      suspendedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      suspendedBy: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      suspensionReason: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      allowedTenantIds: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      proxyEnabled: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      proxyPort: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      proxyProtocols: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      botEnabled: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      botMaxConcurrent: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      workerEnabled: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      location: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      countryCode: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      lastHeartbeatMetrics: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      connectedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      disconnectedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      tunnelConnectedAt: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      tasks: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
      id: {
        sortingEnabled: true,
        width: 300,
        isHidden: true,
      },
    };
    this._sorting = [{ columnName: "name", direction: SortOrder.Asc }];
    makeAutoObservable(this);
  }

  // Verifica si el usuario actual tiene permiso para ver una columna
  isColumnAllowedForUser(columnName: string): boolean {
    const requiredRoles = this.columnRoleRestrictions[columnName];
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // No hay restricción
    }
    return requiredRoles.some((role) => this.rootStore.authStore.hasRole(role));
  }

  get subFilters(): Record<
    string,
    { subFields: string[]; multi: boolean } | undefined
  > {
    return {
      authorizedBy: {
        subFields: ["email", "name"],
        multi: false,
      },
      suspendedBy: {
        subFields: ["email", "name"],
        multi: false,
      },
      tasks: {
        subFields: ["taskType", "correlationId"],
        multi: true,
      },
    };
  }

  get searchFields(): string[] {
    return ["name", "region", "machineCode", "location"];
  }

  get sorting() {
    return this._sorting;
  }

  set sorting(sorting: Sorting[]) {
    this._sorting = sorting;
  }

  get mrtColumnVisibility(): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    for (const [key, info] of Object.entries(this.columnsInfo)) {
      result[key] = !info.isHidden;
    }
    return result;
  }

  setMrtColumnVisibility(visibility: Record<string, boolean>) {
    for (const [key, isVisible] of Object.entries(visibility)) {
      if (this.columnsInfo[key]) {
        this.columnsInfo[key].isHidden = !isVisible;
        // Si se oculta la columna, borrar su filtro (UX: lo que ves es lo que filtras)
        if (!isVisible) {
          this.filtering = this.filtering.filter((f) => f.columnName !== key);
        }
      }
    }
  }

  showAllColumns() {
    for (const key of Object.keys(this.columnsInfo)) {
      this.columnsInfo[key].isHidden = false;
    }
  }

  hideAllColumns() {
    for (const key of Object.keys(this.columnsInfo)) {
      this.columnsInfo[key].isHidden = true;
    }
  }

  // Métodos para estado de UI del DataGrid
  setShowColumnFilters(value: boolean) {
    this.showColumnFilters = value;
  }

  toggleShowColumnFilters() {
    this.showColumnFilters = !this.showColumnFilters;
  }

  setIsFullScreen(value: boolean) {
    this.isFullScreen = value;
  }

  toggleIsFullScreen() {
    this.isFullScreen = !this.isFullScreen;
  }

  setColumnMenuOpen(value: boolean) {
    this.columnMenuOpen = value;
  }

  openColumnMenu() {
    this.columnMenuOpen = true;
  }

  closeColumnMenu() {
    this.columnMenuOpen = false;
  }

  setViewMode(mode: RowClickViewMode) {
    this.viewMode = mode;
  }

  toggleViewMode() {
    this.viewMode = this.viewMode === "drawer" ? "route" : "drawer";
  }

  get mrtColumnSizing(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const [key, info] of Object.entries(this.columnsInfo)) {
      result[key] = info.width;
    }
    return result;
  }

  setMrtColumnSizing(sizing: Record<string, number>) {
    for (const [key, width] of Object.entries(sizing)) {
      if (this.columnsInfo[key]) {
        this.columnsInfo[key].width = width;
      }
    }
  }

  get columnWidths(): ColumnWidthInfo[] {
    return Object.keys(this.columnsInfo).map((key) => {
      return { columnName: key, width: this.columnsInfo[key].width };
    });
  }

  setColumnWidths(columnWidths: ColumnWidthInfo[]) {
    for (const cw of columnWidths) {
      this.columnsInfo[cw.columnName].width = Number(cw.width);
    }
  }

  get columnExtensions(): ColumnExtension[] {
    return Object.keys(this.columnsInfo).map((key) => {
      return {
        columnName: key,
        sortingEnabled: this.columnsInfo[key].sortingEnabled,
      };
    });
  }

  setColumnExtensions(columnExtensions: ColumnExtension[]) {
    for (const ce of columnExtensions) {
      this.columnsInfo[ce.columnName].sortingEnabled = ce.sortingEnabled;
    }
  }

  setColumnOrder(columnOrder: string[]) {
    this.columnOrder = columnOrder;
  }

  resetColumnOrder() {
    this.columnOrder = [...this.initialColumnOrder];
    // Restaurar visibilidad y ancho inicial de columnas
    for (const key of Object.keys(this.columnsInfo)) {
      if (this.initialColumnsInfo[key]) {
        this.columnsInfo[key].isHidden = this.initialColumnsInfo[key].isHidden;
        this.columnsInfo[key].width = this.initialColumnsInfo[key].width;
      }
    }
  }

  setSorting(sorting: Sorting[]) {
    this.sorting = sorting;
  }

  get columnTypes() {
    return {
      name: "String",
      satelliteType: "String",
      region: "String",
      status: "String",
      enabled: "Boolean",
      publicIp: "String",
      lastHeartbeatAt: "DateTime",
      machineCode: "String",
      certificateExpiresAt: "DateTime",
      authTokenLastFour: "String",
      authTokenCreatedAt: "DateTime",
      authTokenRotatedAt: "DateTime",
      authorizedAt: "DateTime",
      authorizedBy: "__Reference",
      suspendedAt: "DateTime",
      suspendedBy: "__Reference",
      suspensionReason: "String",
      allowedTenantIds: "String",
      proxyEnabled: "Boolean",
      proxyPort: "Int",
      proxyProtocols: "String",
      botEnabled: "Boolean",
      botMaxConcurrent: "Int",
      workerEnabled: "Boolean",
      location: "String",
      countryCode: "String",
      lastHeartbeatMetrics: "Json",
      connectedAt: "DateTime",
      disconnectedAt: "DateTime",
      tunnelConnectedAt: "DateTime",
      tasks: "__SubEntity",
      id: "String",
    } as Record<string, string>;
  }

  get filteringColumns() {
    return [
      { columnName: "name", filteringEnabled: true },
      { columnName: "satelliteType", filteringEnabled: true },
      { columnName: "region", filteringEnabled: true },
      { columnName: "status", filteringEnabled: true },
      { columnName: "enabled", filteringEnabled: true },
      { columnName: "publicIp", filteringEnabled: true },
      { columnName: "lastHeartbeatAt", filteringEnabled: true },
      { columnName: "machineCode", filteringEnabled: true },
      { columnName: "certificateExpiresAt", filteringEnabled: true },
      { columnName: "authTokenLastFour", filteringEnabled: true },
      { columnName: "authTokenCreatedAt", filteringEnabled: true },
      { columnName: "authTokenRotatedAt", filteringEnabled: true },
      { columnName: "authorizedAt", filteringEnabled: true },
      { columnName: "authorizedBy", filteringEnabled: true },
      { columnName: "suspendedAt", filteringEnabled: true },
      { columnName: "suspendedBy", filteringEnabled: true },
      { columnName: "suspensionReason", filteringEnabled: true },
      { columnName: "allowedTenantIds", filteringEnabled: true },
      { columnName: "proxyEnabled", filteringEnabled: true },
      { columnName: "proxyPort", filteringEnabled: true },
      { columnName: "proxyProtocols", filteringEnabled: true },
      { columnName: "botEnabled", filteringEnabled: true },
      { columnName: "botMaxConcurrent", filteringEnabled: true },
      { columnName: "workerEnabled", filteringEnabled: true },
      { columnName: "location", filteringEnabled: true },
      { columnName: "countryCode", filteringEnabled: true },
      { columnName: "lastHeartbeatMetrics", filteringEnabled: false },
      { columnName: "connectedAt", filteringEnabled: true },
      { columnName: "disconnectedAt", filteringEnabled: true },
      { columnName: "tunnelConnectedAt", filteringEnabled: true },
      { columnName: "tasks", filteringEnabled: true },
      { columnName: "id", filteringEnabled: true },
    ];
  }

  serverColumn(columnName: string) {
    return columnName;
  }

  setSelectedFilters(filters: GridFilter[]) {
    this.filtering = filters;
  }

  get serverFilters() {
    // Solo aplicar filtros si están visibles (showColumnFilters = true)
    return this.showColumnFilters ? this.filtering : [];
  }

  serverSearch(value: string) {
    return this.searchFields.map((x) => {
      return {
        columnName: x,
        operation: "contains",
        value: value,
      };
    });
  }

  get defaultFilters() {
    return this.filtering;
  }

  get numberColumns() {
    return ["proxyPort", "botMaxConcurrent"];
  }

  get booleanColumns() {
    return ["enabled", "proxyEnabled", "botEnabled", "workerEnabled"];
  }

  get dateColumns() {
    return [
      "lastHeartbeatAt",
      "certificateExpiresAt",
      "authTokenCreatedAt",
      "authTokenRotatedAt",
      "authorizedAt",
      "suspendedAt",
      "connectedAt",
      "disconnectedAt",
      "tunnelConnectedAt",
    ];
  }

  get nullableDateColumns() {
    return [
      "lastHeartbeatAt",
      "authTokenRotatedAt",
      "authorizedAt",
      "suspendedAt",
      "connectedAt",
      "disconnectedAt",
      "tunnelConnectedAt",
    ] as string[];
  }

  get xlsColumns(): { header: string; key: string; width: number }[] {
    const columns = [];
    for (const column of this.columnOrder) {
      if (
        !this.columnsInfo[column].isHidden &&
        (column.length < 2 || column.slice(0, 2) !== "__")
      ) {
        const width = (this.columnsInfo[column].width / 10) * 1.33;
        columns.push({
          header: this.columnTitles[column],
          key: column,
          width,
        });
      }
    }
    return columns;
  }

  get OrderBy() {
    let orderBy = [];
    for (const s of this.sorting) {
      const sortExpression = this.sortExpression[s.columnName];
      if (sortExpression) {
        const ob = sortExpression(
          s.direction === "desc" ? SortOrder.Desc : SortOrder.Asc,
          this.langStore
        );
        orderBy.push(ob);
      } else {
        orderBy.push({
          [s.columnName]:
            s.direction === "desc" ? SortOrder.Desc : SortOrder.Asc,
        });
      }
    }
    return orderBy;
  }

  get columnTitles(): Record<string, string> {
    return {
      name: `${this.langStore.SatelliteMachineNameLabel}`,
      machineCode: `${this.langStore.SatelliteMachineMachineCodeLabel}`,
      satelliteType: `${this.langStore.SatelliteMachineSatelliteTypeLabel}`,
      region: `${this.langStore.SatelliteMachineRegionLabel}`,
      certificateExpiresAt: `${this.langStore.SatelliteMachineCertificateExpiresAtLabel}`,
      authTokenLastFour: `${this.langStore.SatelliteMachineAuthTokenLastFourLabel}`,
      authTokenCreatedAt: `${this.langStore.SatelliteMachineAuthTokenCreatedAtLabel}`,
      authTokenRotatedAt: `${this.langStore.SatelliteMachineAuthTokenRotatedAtLabel}`,
      status: `${this.langStore.SatelliteMachineStatusLabel}`,
      authorizedAt: `${this.langStore.SatelliteMachineAuthorizedAtLabel}`,
      authorizedBy: `${this.langStore.SatelliteMachineAuthorizedByLabel}`,
      suspendedAt: `${this.langStore.SatelliteMachineSuspendedAtLabel}`,
      suspendedBy: `${this.langStore.SatelliteMachineSuspendedByLabel}`,
      suspensionReason: `${this.langStore.SatelliteMachineSuspensionReasonLabel}`,
      enabled: `${this.langStore.SatelliteMachineEnabledLabel}`,
      allowedTenantIds: `${this.langStore.SatelliteMachineAllowedTenantIdsLabel}`,
      proxyEnabled: `${this.langStore.SatelliteMachineProxyEnabledLabel}`,
      proxyPort: `${this.langStore.SatelliteMachineProxyPortLabel}`,
      proxyProtocols: `${this.langStore.SatelliteMachineProxyProtocolsLabel}`,
      botEnabled: `${this.langStore.SatelliteMachineBotEnabledLabel}`,
      botMaxConcurrent: `${this.langStore.SatelliteMachineBotMaxConcurrentLabel}`,
      workerEnabled: `${this.langStore.SatelliteMachineWorkerEnabledLabel}`,
      publicIp: `${this.langStore.SatelliteMachinePublicIpLabel}`,
      location: `${this.langStore.SatelliteMachineLocationLabel}`,
      countryCode: `${this.langStore.SatelliteMachineCountryCodeLabel}`,
      lastHeartbeatAt: `${this.langStore.SatelliteMachineLastHeartbeatAtLabel}`,
      lastHeartbeatMetrics: `${this.langStore.SatelliteMachineLastHeartbeatMetricsLabel}`,
      connectedAt: `${this.langStore.SatelliteMachineConnectedAtLabel}`,
      disconnectedAt: `${this.langStore.SatelliteMachineDisconnectedAtLabel}`,
      tunnelConnectedAt: `${this.langStore.SatelliteMachineTunnelConnectedAtLabel}`,
      tasks: `${this.langStore.SatelliteMachineTasksLabel}`,
      id: `Id`,
    };
  }

  // Un date (día de calendario) filtra en su propia columna, sin zona ni hora
  // ('calendarDate'); un dateTime sigue siendo 'date'.
  private getMrtColumnType(
    type: string
  ): "string" | "number" | "date" | "calendarDate" | "boolean" {
    if (type === "Int" || type === "Decimal") return "number";
    if (type === "Date") return "calendarDate";
    if (type === "DateTime") return "date";
    if (type === "Boolean") return "boolean";
    return "string";
  }

  get dataGridColumns(): DataGridColumn<Record<string, any>>[] {
    // Usar initialColumnOrder (orden fijo) para definir columnas
    // MRT maneja reorden visual con prop columnOrder separada
    // Esto permite que "Reset Order" funcione correctamente
    const nullableDateColumns = this.nullableDateColumns;
    return this.initialColumnOrder
      .filter(
        (name) =>
          (name.length < 2 || name.slice(0, 2) !== "__") &&
          this.isColumnAllowedForUser(name)
      )
      .map((name) => {
        const columnType = this.columnTypes[name];
        const filterCol = this.filteringColumns.find(
          (f: any) => f.columnName === name
        );
        const baseColumn = {
          header: this.columnTitles[name] || name,
          size: this.columnsInfo[name]?.width || 150,
          columnType: this.getMrtColumnType(columnType),
          nullable: nullableDateColumns.includes(name),
          filterEnabled: filterCol?.filteringEnabled ?? true,
          sortingEnabled: this.columnsInfo[name]?.sortingEnabled ?? true,
        };

        // JSON fields need accessorFn to stringify the value
        if (columnType === "Json") {
          return {
            ...baseColumn,
            id: name,
            accessorFn: (row: any) => {
              const val = row[name];
              if (val == null) return "";
              const s = JSON.stringify(val);
              return s.length > 100 ? s.substring(0, 100) + "..." : s;
            },
          };
        }

        return {
          ...baseColumn,
          accessorKey: name,
        };
      });
  }

  // Lista de columnas para el ColumnMenu (usado en ListToolbox)
  get columnMenuItems(): { id: string; header: string }[] {
    return this.initialColumnOrder
      .filter(
        (name) =>
          (name.length < 2 || name.slice(0, 2) !== "__") &&
          this.isColumnAllowedForUser(name)
      )
      .map((name) => ({
        id: name,
        header: this.columnTitles[name] || name,
      }));
  }

  satelliteMachineFormat(e: SatelliteMachine) {
    return {
      name: e.name,
      satelliteType: e.satelliteType,
      region: e.region,
      status: e.status,
      enabled: e.enabled,
      publicIp: e.publicIp,
      lastHeartbeatAt: e.lastHeartbeatAt ? new Date(e.lastHeartbeatAt) : "",
      machineCode: e.machineCode,
      certificateExpiresAt: new Date(e.certificateExpiresAt),
      authTokenLastFour: e.authTokenLastFour,
      authTokenCreatedAt: new Date(e.authTokenCreatedAt),
      authTokenRotatedAt: e.authTokenRotatedAt
        ? new Date(e.authTokenRotatedAt)
        : "",
      authorizedAt: e.authorizedAt ? new Date(e.authorizedAt) : "",
      authorizedBy: e.authorizedBy
        ? userDisplayId(e.authorizedBy, this.langStore)
        : "",
      suspendedAt: e.suspendedAt ? new Date(e.suspendedAt) : "",
      suspendedBy: e.suspendedBy
        ? userDisplayId(e.suspendedBy, this.langStore)
        : "",
      suspensionReason: e.suspensionReason,
      allowedTenantIds: e.allowedTenantIds,
      proxyEnabled: e.proxyEnabled,
      proxyPort: e.proxyPort,
      proxyProtocols: e.proxyProtocols,
      botEnabled: e.botEnabled,
      botMaxConcurrent: e.botMaxConcurrent,
      workerEnabled: e.workerEnabled,
      location: e.location,
      countryCode: e.countryCode,
      lastHeartbeatMetrics: e.lastHeartbeatMetrics
        ? (() => {
            const s = JSON.stringify(e.lastHeartbeatMetrics);
            return s.length > 100 ? s.substring(0, 100) + "..." : s;
          })()
        : "",
      connectedAt: e.connectedAt ? new Date(e.connectedAt) : "",
      disconnectedAt: e.disconnectedAt ? new Date(e.disconnectedAt) : "",
      tunnelConnectedAt: e.tunnelConnectedAt
        ? new Date(e.tunnelConnectedAt)
        : "",
      tasks: e.tasks
        ? e.tasks.map((x: any) => satelliteTaskDisplayId(x, this.langStore))
        : [],
      id: e.id,
    };
  }

  get gridState(): any {
    // Solo guardar filtros si están habilitados Y tienen valores
    const hasActiveFilters =
      this.showColumnFilters && this.filtering.length > 0;
    return {
      columnOrder: this.columnOrder,
      columnsInfo: this.columnsInfo,
      sorting: this.sorting,
      filtering: hasActiveFilters ? this.filtering : [],
      // showColumnFilters solo true si hay filtros activos
      showColumnFilters: hasActiveFilters,
      viewMode: this.viewMode,
      VERSION,
    };
  }

  setGridState(gridState: SatelliteMachineListProfile) {
    // Nunca debe fallar la carga del profile - el cliente manda, el profile sugiere
    try {
      if (!gridState || typeof gridState !== "object") return;
      // No rechazar por VERSION diferente - validamos cada campo individualmente
      // Esto permite migrar profiles de versiones anteriores

      const validColumns = new Set(Object.keys(this.columnsInfo));

      if (gridState.columnsInfo && typeof gridState.columnsInfo === "object") {
        const mergedColumnsInfo: ColumnInfo = {};

        for (const [key, defaultValue] of Object.entries(this.columnsInfo)) {
          mergedColumnsInfo[key] = { ...defaultValue };
        }

        for (const [key, savedValue] of Object.entries(gridState.columnsInfo)) {
          if (
            validColumns.has(key) &&
            typeof savedValue === "object" &&
            savedValue !== null
          ) {
            const defaultValue = mergedColumnsInfo[key];

            mergedColumnsInfo[key] = {
              sortingEnabled:
                typeof savedValue.sortingEnabled === "boolean"
                  ? savedValue.sortingEnabled
                  : defaultValue.sortingEnabled,

              width:
                typeof savedValue.width === "number" &&
                savedValue.width > 0 &&
                isFinite(savedValue.width)
                  ? savedValue.width
                  : defaultValue.width,

              isHidden:
                typeof savedValue.isHidden === "boolean"
                  ? savedValue.isHidden
                  : defaultValue.isHidden,
            };
          }
        }

        this.columnsInfo = mergedColumnsInfo;
      }

      if (Array.isArray(gridState.columnOrder)) {
        const validOrderColumns = gridState.columnOrder.filter(
          (col) => typeof col === "string" && validColumns.has(col)
        );
        const newColumns = Array.from(validColumns).filter(
          (col) => !gridState.columnOrder.includes(col)
        );
        this.columnOrder = [...validOrderColumns, ...newColumns];
      }

      if (Array.isArray(gridState.sorting)) {
        this.sorting = gridState.sorting.filter(
          (sort) =>
            typeof sort === "object" &&
            sort !== null &&
            typeof sort.columnName === "string" &&
            validColumns.has(sort.columnName) &&
            (sort.direction === "asc" || sort.direction === "desc")
        );
        if (this.sorting.length === 0) {
          this.sorting = [{ columnName: "name", direction: SortOrder.Asc }];
        }
      }

      if (Array.isArray(gridState.filtering)) {
        const nullableDateColumns = this.nullableDateColumns;
        this.filtering = gridState.filtering.filter(
          (filter) =>
            typeof filter === "object" &&
            filter !== null &&
            typeof filter.columnName === "string" &&
            validColumns.has(filter.columnName) &&
            typeof filter.operation === "string" &&
            filter.hasOwnProperty("value") &&
            // "Vacío" y "No vacío" solo en una fecha que puede ser nula. Un perfil
            // guardado cuando la columna era otra cosa no puede dejar un filtro que
            // el server rechaza en cada carga (el DataGrid también lo descarta, pero
            // el perfil puede llegar acá antes).
            isOperatorAvailable(
              this.getMrtColumnType(this.columnTypes[filter.columnName]),
              filter.operation as FilterOperator,
              nullableDateColumns.includes(filter.columnName)
            )
        );
      }

      // Si hay filtros guardados, mostrar los filtros automáticamente
      // Si el profile guardó showColumnFilters, usar ese valor
      if (typeof gridState.showColumnFilters === "boolean") {
        this.showColumnFilters = gridState.showColumnFilters;
      } else if (this.filtering.length > 0) {
        // Compatibilidad: si hay filtros pero no se guardó showColumnFilters, activarlos
        this.showColumnFilters = true;
      }

      if (gridState.viewMode === "route" || gridState.viewMode === "drawer") {
        this.viewMode = gridState.viewMode;
      }
    } catch (error) {
      // Silenciar errores de carga de profile - usar valores por defecto
      console.warn(
        "[GridDataStore] Error loading profile, using defaults:",
        error
      );
    }
  }
}
